/**
 * Report Viewer Component
 *
 * Modal for viewing generated reports with sections and elements.
 * Renders different element types (text, list, table, timeline).
 */

import { useState } from 'react';
import type { GeneratedReport, ReportSection, ReportElement, DataFreshness } from '../../types';

interface ReportViewerProps {
  report: GeneratedReport;
  onClose: () => void;
}

// Freshness indicator colors
const FRESHNESS_COLORS: Record<DataFreshness, { bg: string; text: string; label: string }> = {
  current: { bg: 'bg-green-100', text: 'text-green-800', label: 'Current' },
  recent: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Recent' },
  stale: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Stale' },
  outdated: { bg: 'bg-red-100', text: 'text-red-800', label: 'Outdated' },
  estimated: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Estimated' },
};

function FreshnessBadge({ freshness }: { freshness: DataFreshness | null }) {
  if (!freshness) return null;
  const colors = FRESHNESS_COLORS[freshness];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors.bg} ${colors.text}`}>
      {colors.label}
    </span>
  );
}

function formatDate(dateString: string | null): string {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
}

function ElementValue({ element }: { element: ReportElement }) {
  const { data_type, value } = element;

  if (value === null || value === undefined) {
    return <span className="text-gray-400 italic">No data available</span>;
  }

  // Handle different data types
  switch (data_type) {
    case 'text':
      return <p className="text-gray-700 whitespace-pre-wrap">{String(value)}</p>;

    case 'number':
      return <span className="text-2xl font-semibold text-gray-900">{String(value)}</span>;

    case 'currency':
      if (typeof value === 'string') {
        return <span className="text-lg font-medium text-gray-900">{value}</span>;
      }
      if (typeof value === 'number') {
        return (
          <span className="text-lg font-medium text-gray-900">
            ${value.toLocaleString()}
          </span>
        );
      }
      return <span className="text-lg font-medium text-gray-900">{String(value)}</span>;

    case 'percentage':
      return <span className="text-lg font-medium text-gray-900">{String(value)}</span>;

    case 'list':
      if (!Array.isArray(value)) {
        return <p className="text-gray-700">{String(value)}</p>;
      }
      return (
        <ul className="list-disc list-inside space-y-1 text-gray-700">
          {value.map((item, idx) => (
            <li key={idx}>{String(item)}</li>
          ))}
        </ul>
      );

    case 'table': {
      if (!Array.isArray(value) || value.length === 0) {
        return <span className="text-gray-400 italic">No data available</span>;
      }
      // Get column headers from first item
      const headers = Object.keys(value[0] as object);
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {headers.map((header) => (
                  <th
                    key={header}
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    {header.replace(/_/g, ' ')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(value as Record<string, unknown>[]).map((row, idx) => (
                <tr key={idx}>
                  {headers.map((header) => (
                    <td key={header} className="px-3 py-2 text-sm text-gray-700">
                      {String(row[header] || '-')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    case 'timeline':
      if (!Array.isArray(value) || value.length === 0) {
        return <span className="text-gray-400 italic">No timeline data</span>;
      }
      return (
        <div className="space-y-3">
          {(value as Array<{ date?: string; event?: string }>).map((item, idx) => (
            <div key={idx} className="flex items-start gap-3">
              <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-primary-500" />
              <div>
                {item.date && (
                  <span className="text-xs font-medium text-gray-500">{item.date}</span>
                )}
                <p className="text-sm text-gray-700">{item.event || String(item)}</p>
              </div>
            </div>
          ))}
        </div>
      );

    default:
      // Fallback for unknown types - try to render as JSON or string
      if (typeof value === 'object') {
        return (
          <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto">
            {JSON.stringify(value, null, 2)}
          </pre>
        );
      }
      return <p className="text-gray-700">{String(value)}</p>;
  }
}

function SectionAccordion({
  section,
  isExpanded,
  onToggle,
}: {
  section: ReportSection;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <h3 className="text-sm font-semibold text-gray-900">{section.name}</h3>
        </div>
        <span className="text-xs text-gray-500">{section.elements.length} elements</span>
      </button>

      {isExpanded && (
        <div className="px-4 py-4 space-y-6 bg-white">
          {section.description && (
            <p className="text-sm text-gray-500 italic">{section.description}</p>
          )}

          {section.elements.map((element, idx) => (
            <div key={idx} className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-700">{element.name}</h4>
                <FreshnessBadge freshness={element.freshness} />
              </div>
              <ElementValue element={element} />
              {element.last_updated && (
                <p className="text-xs text-gray-400">Updated: {formatDate(element.last_updated)}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ReportViewer({ report, onClose }: ReportViewerProps) {
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set([0]));

  const toggleSection = (index: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedSections(new Set(report.sections.map((_, i) => i)));
  };

  const collapseAll = () => {
    setExpandedSections(new Set());
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex-shrink-0 border-b border-gray-200 px-6 py-4">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{report.title}</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {report.company_name} | Generated {formatDate(report.generated_at)}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Metadata badges */}
            <div className="flex items-center gap-4 mt-3">
              {report.confidence_score !== undefined && (
                <div className="flex items-center gap-1 text-sm">
                  <span className="text-gray-500">Confidence:</span>
                  <span className={`font-medium ${
                    report.confidence_score >= 0.7 ? 'text-green-600' :
                    report.confidence_score >= 0.4 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {Math.round(report.confidence_score * 100)}%
                  </span>
                </div>
              )}
              <div className="flex items-center gap-1 text-sm">
                <span className="text-gray-500">Sections:</span>
                <span className="font-medium text-gray-700">{report.sections.length}</span>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="flex-shrink-0 px-6 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-4">
            <button
              onClick={expandAll}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              Collapse All
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {report.sections.map((section, idx) => (
              <SectionAccordion
                key={idx}
                section={section}
                isExpanded={expandedSections.has(idx)}
                onToggle={() => toggleSection(idx)}
              />
            ))}
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 border-t border-gray-200 px-6 py-3 bg-gray-50">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-400">
                Report ID: {report.id}
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReportViewer;
